import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { API_BASE_URL } from "../../config";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import SelectDropdownIcon from "../../components/SelectDropdownIcon";
import {
  PAGE_HEADER_TITLE_SX,
  STICKY_PAGE_HEADER_SUBTITLE_SX,
  STICKY_PAGE_HEADER_TEXT_COLUMN_SX,
} from "../../components/layout/stickyPageHeaderTokens";
import { setCurrentProject } from "../../store/slices/projectSlice";
import {
  buildGate0ApiPayload,
  saveGate0,
  loadDraftProjectId,
  clearDraftProjectId,
} from "../../utils/gate0Storage";

const PAGE_HEIGHT = "100%";

const GATES = ["GATE-0", "GATE-1", "GATE-2", "GATE-3", "GATE-4"];
const GATE_ROW_PADDING_LEFT_PX = 29;
const ACTIVE_GATE_INDEX = 0;

/**
 * Back-arrow placement, measured to the arrow's ink rather than to its hit
 * target: 24px from the page's left edge, 16px to the heading's first letter.
 * Two corrections stand between those numbers and the CSS below:
 *
 *   - IntakeHeaderBackButton is a 32x32 button holding a 20x16 arrow at
 *     left: 6px, leaving 6px of dead space on either side of the ink.
 *   - Lato renders its capitals with ~1px of left side bearing, so the
 *     heading's text box starts 1px before the letter the eye sees.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const BACK_ARROW_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;

/**
 * Title card — same dimensions as the Gate 2 "Define Scope" header: 16px of
 * vertical padding, 24px of horizontal padding, and the shared title/subtitle
 * type ramp. Only the back-arrow spacing differs, since this page carries the
 * newer 24/16 standard that Gate 2 has yet to adopt.
 */
const HERO_BAND_PADDING_Y_PX = 16;

const GATE_TAB = {
  width: { xs: 80, md: 93 },
  height: 50,
  gap: 4,
  fontFamily: "Lato, sans-serif",
  fontSize: { xs: 13, md: 14 },
  lineHeight: "18px",
  letterSpacing: "1px",
  inactive: { fontWeight: 400, color: "#555555" },
  active: { fontWeight: 700, color: "#555555" },
  disabled: { fontWeight: 400, color: "#BBBBBB" },
};

const COLORS = {
  pageBg: "#F5F5F5",
  surface: "#FFFFFF",
  border: "#E6E6E6",
  primary20: "#F0F6FF",
  grey50: "#707070",
  grey70: "#383838",
  text: "#383838",
  textMuted: "#707070",
  textSecondary: "#555555",
  primary: "#005BF0",
  required: "#D6441F",
};

const EMPTY_FORM = {
  appName: "",
  version: "",
  ownerName: "",
  environment: "",
  criticality: "",
  domain: "",
  intendedUse: "",
  frameworks: "",
};

const CRITICALITY_OPTIONS = ["Low", "Medium", "High", "Critical"];
const DOMAIN_OPTIONS = ["Financial Services", "Healthcare", "Retail", "Cybersecurity", "Other"];
const ENVIRONMENT_OPTIONS = ["Development", "Staging", "Production"];

const FIELD_LIMITS = {
  appName: 100,
  version: 60,
  ownerName: 100,
  intendedUse: 100,
  frameworks: 100,
};

//const LIMIT_EXCEEDED_MSG = "Character limit exceeded";
const ALPHA_NUMERIC_INVALID_CHARS = /[^a-zA-Z0-9 ]/g;
const VERSION_INVALID_CHARS = /[^a-zA-Z0-9.]/g;

function sanitizeAlphaNumeric(value) {
  return (value || "").replace(ALPHA_NUMERIC_INVALID_CHARS, "");
}

function sanitizeVersion(value) {
  return (value || "").replace(VERSION_INVALID_CHARS, "");
}

const FIELD_SANITIZERS = {
  appName: sanitizeAlphaNumeric,
  ownerName: sanitizeAlphaNumeric,
  version: sanitizeVersion,
};

function limitHelperText(field, value, limitState, fieldError) {
  if (fieldError) return fieldError;
  const max = FIELD_LIMITS[field];
  const len = value.length;
  const mode = limitState[field]?.mode;
  if (mode === "paste" && len > max) {
    return `${len}/${max}`;
  }
  if (mode === "type") {
    return `${max}/${max}`;
  }
  if (len >= max) return `${max}/${max}`;
  return `${len}/${max}`;
}

function Gate0LimitedTextField({
  field,
  value,
  onLimitedChange,
  onPasteField,
  limitState,
  fieldError,
  inputSx,
  ...textFieldProps
}) {
  const max = FIELD_LIMITS[field];
  const len = value.length;
  const mode = limitState[field]?.mode;
  const overLimit = len > max;

  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      onChange={onLimitedChange(field)}
      onPaste={onPasteField(field)}
      error={Boolean(fieldError) || overLimit || mode === "type"}
      helperText={limitHelperText(field, value, limitState, fieldError)}
      FormHelperTextProps={{ sx: { marginLeft: 0, marginRight: 0, mx: 0, px: 0 } }}
      sx={inputSx}
      {...textFieldProps}
    />
  );
}

function gateTabState(index, activeIndex) {
  if (index === activeIndex) return "active";
  if (index < activeIndex) return "inactive";
  return "disabled";
}

function GateTab({ label, state }) {
  const tabStyle = GATE_TAB[state];
  const isDisabled = state === "disabled";
  return (
    <Box
      component="button"
      type="button"
      disabled={isDisabled}
      sx={{
        width: GATE_TAB.width,
        minWidth: GATE_TAB.width,
        maxWidth: GATE_TAB.width,
        height: "100%",
        maxHeight: GATE_TAB.height,
        px: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: 0,
        borderBottom: state === "active" ? "2px solid #005BF0" : "2px solid transparent",
        bgcolor: "transparent",
        cursor: isDisabled ? "default" : "pointer",
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontFamily: GATE_TAB.fontFamily,
          fontSize: GATE_TAB.fontSize,
          lineHeight: GATE_TAB.lineHeight,
          letterSpacing: GATE_TAB.letterSpacing,
          textTransform: "uppercase",
          textAlign: "center",
          fontWeight: tabStyle.fontWeight,
          color: tabStyle.color,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const fieldGroupSx = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  height: "100%",
};

const GATE0_BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const GATE0_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";

const GATE0_PRIMARY_BTN_BG_DEFAULT =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const GATE0_PRIMARY_BTN_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const GATE0_PRIMARY_BTN_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const gate0StartIntakeBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  height: 40,
  minHeight: 40,
  minWidth: 110,
  py: "8px",
  px: "16px",
  gap: "4px",
  boxSizing: "border-box",
  color: "#FFFFFF",
  borderRadius: "4px",
  border: "1px solid transparent",
  boxShadow: GATE0_BTN_SHADOW,
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: GATE0_PRIMARY_BTN_BG_DEFAULT,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: GATE0_PRIMARY_BTN_BG_HOVER,
    boxShadow: GATE0_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${GATE0_PRIMARY_BTN_BG_DEFAULT} !important`,
    border: "2px solid #005FCC",
    boxShadow: GATE0_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${GATE0_PRIMARY_BTN_BG_DEFAULT} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${GATE0_PRIMARY_BTN_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: GATE0_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: GATE0_BTN_SHADOW,
    opacity: 1,
    cursor: "not-allowed",
  },
};

/** Footer ghost button — matches the Back control on the other gate pages. */
const gate0FooterGhostBtnSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  border: `1px solid ${COLORS.primary}`,
  bgcolor: COLORS.surface,
  backgroundColor: COLORS.surface,
  backgroundImage: "none",
  color: COLORS.primary,
  boxShadow: GATE0_BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: COLORS.primary20,
    backgroundColor: COLORS.primary20,
    backgroundImage: "none",
    border: `1px solid ${COLORS.primary}`,
    color: COLORS.primary,
    boxShadow: GATE0_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: `${COLORS.surface} !important`,
    backgroundColor: `${COLORS.surface} !important`,
    backgroundImage: "none !important",
    border: `2px solid ${COLORS.primary}`,
    color: COLORS.primary,
    boxShadow: GATE0_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: `${COLORS.surface} !important`,
    backgroundColor: `${COLORS.surface} !important`,
    backgroundImage: "none !important",
    border: `2px solid ${COLORS.primary}`,
    color: COLORS.primary,
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: `${COLORS.surface} !important`,
    backgroundColor: `${COLORS.surface} !important`,
    backgroundImage: "none !important",
    border: `1px solid ${COLORS.primary}`,
    color: COLORS.primary,
    boxShadow: GATE0_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: `1px solid ${COLORS.border}`,
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

const GATE0_FORM_CARD_SX = {
  maxWidth: 1256,
  width: "100%",
  mx: "auto",
  bgcolor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: "4px",
  boxSizing: "border-box",
  p: { xs: 2.5, md: "32px 24px" },
};

const fieldLabelSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 14,
  lineHeight: "21px",
  letterSpacing: 0,
  color: COLORS.grey50,
  display: "block",
};

function RequiredLabel({ children }) {
  return (
    <Typography component="span" sx={fieldLabelSx}>
      <Box component="span" sx={{ color: COLORS.required, mr: 0.25 }}>
        *
      </Box>
      {children}
    </Typography>
  );
}

const GATE0_INPUT_INSET_SHADOW = "inset 0px 2px 3px 1px #55555533";

const gate0InputOutlineSx = {
  borderColor: COLORS.grey50,
  borderWidth: "1px",
};

const gate0InputSx = {
  width: "100%",
  maxWidth: 592,
  "& .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    borderRadius: "4px",
    fontSize: "16px",
    bgcolor: COLORS.surface,
    boxShadow: GATE0_INPUT_INSET_SHADOW,
    "& fieldset": gate0InputOutlineSx,
    "&:hover fieldset": gate0InputOutlineSx,
    "&.Mui-focused fieldset": gate0InputOutlineSx,
    "&.Mui-error fieldset": { borderColor: COLORS.required, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    py: "8px",
    px: "10px",
    boxSizing: "border-box",
  },
  "& .MuiSelect-select": {
    minHeight: "unset !important",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-icon": {
    width: 24,
    height: 24,
    right: 10,
    top: "calc(50% - 12px)",
    pointerEvents: "none",
  },
  "& .MuiInputBase-input::placeholder": { color: COLORS.grey50, opacity: 1, fontSize: "16px" },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    marginRight: 0,
    mx: 0,
    px: 0,
    textAlign: "right",
  },
};

const gate0FrameworksInputSx = {
  ...gate0InputSx,
  maxWidth: 1208,
};

export default function Gate0IntakePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));
  const [errors, setErrors] = useState({});
  const [limitState, setLimitState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [draftProjectId, setDraftProjectId] = useState(() => loadDraftProjectId());
  const pasteFieldRef = useRef(null);

  const updateField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (submitError) setSubmitError("");
  };

  const markPasteField = (field) => () => {
    pasteFieldRef.current = field;
  };

  const handleLimitedChange = (field) => (e) => {
    const max = FIELD_LIMITS[field];
    const rawValue = e.target.value;
    const sanitize = FIELD_SANITIZERS[field];
    const value = sanitize ? sanitize(rawValue) : rawValue;
    const isPaste = pasteFieldRef.current === field;
    pasteFieldRef.current = null;

    setForm((prev) => {
      const prevValue = prev[field] || "";
      const isAddingChars = value.length > prevValue.length;

      if (!isPaste && value.length > max && isAddingChars) {
        setLimitState((limitPrev) => ({ ...limitPrev, [field]: { mode: "type" } }));
        return prev;
      }

      setLimitState((limitPrev) => ({
        ...limitPrev,
        [field]: value.length > max ? { mode: "paste" } : { mode: null },
      }));
      return { ...prev, [field]: value };
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (submitError) setSubmitError("");
  };

  const validate = () => {
    const next = {};
    if (!form.appName.trim()) next.appName = "App name is required";
    if (!form.version.trim()) next.version = "Version is required";
    if (!form.ownerName.trim()) next.ownerName = "Application owner is required";
    Object.entries(FIELD_LIMITS).forEach(([field, max]) => {
      const len = (form[field] || "").length;
      if (len > max) {
        next[field] = `${len}/${max}`;
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleBack = () => navigate("/home");

  const handleStartIntake = async () => {
    if (!validate()) return;
    const gate0 = buildGate0ApiPayload(form, {});
    try {
      setSubmitting(true);
      setSubmitError("");
      let created;

      if (draftProjectId) {
        const res = await axios.put(`${API_BASE_URL}/projects/${draftProjectId}`, {
          project_name: gate0.app_name,
          gate0_data: gate0,
        });
        if (!res.data?.success) {
          setSubmitError(res.data?.error || "Failed to update application.");
          return;
        }
        created = res.data.data;
      } else {
        const res = await axios.post(`${API_BASE_URL}/projects`, {
          project_name: gate0.app_name,
          gate0_data: gate0,
        });
        if (!res.data?.success) {
          setSubmitError(res.data?.error || "Failed to create application.");
          return;
        }
        created = res.data.data;
      }

      dispatch(setCurrentProject({ id: created.id, project_name: created.project_name }));
      saveGate0(created.id, gate0);
      clearDraftProjectId();

      navigate(`/framework/${created.id}`, { state: { gate0 } });
    } catch (err) {
      setSubmitError(err.response?.data?.error || "Failed to create application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        height: PAGE_HEIGHT,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: COLORS.pageBg,
        mx: -0,
      }}
    >
      {/* Gate tabs */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: COLORS.surface,
          borderBottom: `1px solid ${COLORS.border}`,
          pl: { xs: 2, md: `${GATE_ROW_PADDING_LEFT_PX}px` },
          pr: { xs: 2, md: 3 },
          height: GATE_TAB.height,
          minHeight: GATE_TAB.height,
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "stretch", height: GATE_TAB.height, gap: `${GATE_TAB.gap}px` }}>
          {GATES.map((g, i) => (
            <GateTab key={g} label={g} state={gateTabState(i, ACTIVE_GATE_INDEX)} />
          ))}
        </Box>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          flexShrink: 0,
          boxSizing: "border-box",
          width: "100%",
          bgcolor: COLORS.surface,
          borderBottom: `1px solid ${COLORS.border}`,
          pl: `${BACK_ARROW_INSET_LEFT_PX}px`,
          pr: { xs: 2, md: 3 },
          py: `${HERO_BAND_PADDING_Y_PX}px`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: `${BACK_ARROW_GAP_RIGHT_PX}px`,
            width: "100%",
            minWidth: 0,
          }}
        >
          <IntakeHeaderBackButton onClick={handleBack} aria-label="Back" />
          <Box sx={STICKY_PAGE_HEADER_TEXT_COLUMN_SX}>
            <Typography component="h1" sx={PAGE_HEADER_TITLE_SX}>
              App Identity
            </Typography>
            <Typography component="p" sx={STICKY_PAGE_HEADER_SUBTITLE_SX}>
              Enter the core details of your AI application. These details will appear on the project
              card and help identify the project throughout the platform.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable form — grey page bg with centered white card (Figma) */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          bgcolor: COLORS.pageBg,
          px: { xs: 2, md: 4 },
          pt: "32px",
          pb: "32px",
        }}
      >
        <Box sx={GATE0_FORM_CARD_SX}>
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 900,
              fontSize: 24,
              lineHeight: "48px",
              color: COLORS.grey70,
              mb: 3,
            }}
          >
            Enter Gate 0 Data
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={fieldGroupSx}>
                <RequiredLabel>App Name</RequiredLabel>
                <Gate0LimitedTextField
                  field="appName"
                  placeholder="e.g. Loan IQ Decision Engine"
                  value={form.appName}
                  onLimitedChange={handleLimitedChange}
                  onPasteField={markPasteField}
                  limitState={limitState}
                  fieldError={errors.appName}
                  inputSx={gate0InputSx}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={fieldGroupSx}>
                <RequiredLabel>Version</RequiredLabel>
                <Gate0LimitedTextField
                  field="version"
                  placeholder="e.g.v2.4.1"
                  value={form.version}
                  onLimitedChange={handleLimitedChange}
                  onPasteField={markPasteField}
                  limitState={limitState}
                  fieldError={errors.version}
                  inputSx={gate0InputSx}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={fieldGroupSx}>
                <RequiredLabel>Application Owner</RequiredLabel>
                <Gate0LimitedTextField
                  field="ownerName"
                  placeholder="Name or Team Name"
                  value={form.ownerName}
                  onLimitedChange={handleLimitedChange}
                  onPasteField={markPasteField}
                  limitState={limitState}
                  fieldError={errors.ownerName}
                  inputSx={gate0InputSx}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={fieldGroupSx}>
                <Typography sx={fieldLabelSx}>Environment</Typography>
                <FormControl fullWidth size="small" sx={gate0InputSx}>
                  <Select
                    value={form.environment}
                    onChange={updateField("environment")}
                    IconComponent={SelectDropdownIcon}
                    displayEmpty
                    renderValue={(v) => v || <Typography sx={{ color: COLORS.grey50, fontSize: "16px" }}>Select environment</Typography>}
                  >
                    {ENVIRONMENT_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={fieldGroupSx}>
                <Typography sx={fieldLabelSx}>Criticality</Typography>
                <FormControl fullWidth size="small" sx={gate0InputSx}>
                  <Select
                    value={form.criticality}
                    onChange={updateField("criticality")}
                    IconComponent={SelectDropdownIcon}
                    displayEmpty
                    renderValue={(v) => v || <Typography sx={{ color: COLORS.grey50, fontSize: "16px" }}>Select criticality level</Typography>}
                  >
                    {CRITICALITY_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={fieldGroupSx}>
                <Typography sx={fieldLabelSx}>Domain</Typography>
                <FormControl fullWidth size="small" sx={gate0InputSx}>
                  <Select
                    value={form.domain}
                    onChange={updateField("domain")}
                    IconComponent={SelectDropdownIcon}
                    displayEmpty
                    renderValue={(v) => v || <Typography sx={{ color: COLORS.grey50, fontSize: "16px" }}>Select business domain</Typography>}
                  >
                    {DOMAIN_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} >
              <Box sx={fieldGroupSx}>
                <Typography sx={fieldLabelSx}>Intended use</Typography>
                <Gate0LimitedTextField
                  field="intendedUse"
                  placeholder="e.g. Automate loan approvals"
                  value={form.intendedUse}
                  onLimitedChange={handleLimitedChange}
                  onPasteField={markPasteField}
                  limitState={limitState}
                  fieldError={errors.intendedUse}
                  inputSx={gate0FrameworksInputSx}
                />
              </Box>
            </Grid>

            <Grid item xs={12} >
              <Box sx={fieldGroupSx}>
                <Typography sx={fieldLabelSx}>Frameworks (Comma separated)</Typography>
                <Gate0LimitedTextField
                  field="frameworks"
                  placeholder="e.g. EU AI Act, GDPR, ECOA/FCRA"
                  value={form.frameworks}
                  onLimitedChange={handleLimitedChange}
                  onPasteField={markPasteField}
                  limitState={limitState}
                  fieldError={errors.frameworks}
                  inputSx={gate0FrameworksInputSx}
                />
              </Box>
            </Grid>
          </Grid>

          {submitError && (
            <Typography sx={{ mt: 2, fontSize: 13, color: COLORS.required }}>{submitError}</Typography>
          )}

          {/*<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/home")}
              disabled={submitting}
              sx={gate0CancelBtnSx}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleStartIntake}
              disabled={submitting}
              sx={gate0StartIntakeBtnSx}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Start Intake"}
            </Button>
          </Box>*/}
        </Box>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, md: 4 },
          py: 2,
          bgcolor: COLORS.surface,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <Button
          disableElevation
          disableRipple
          onClick={handleBack}
          disabled={submitting}
          sx={gate0FooterGhostBtnSx}
        >
          Back
        </Button>

        <Button
          variant="contained"
          disableElevation
          disableRipple
          onClick={handleStartIntake}
          disabled={submitting}
          sx={gate0StartIntakeBtnSx}
        >
          {submitting ? (
            <CircularProgress size={20} sx={{ color: "white" }} /> 
          ) : (
          "Start Intake"
          )}
        </Button>
        </Box>
    </Box>
  );
}
